import { Link, useSearch } from '@tanstack/react-router';
import { Construction, ShieldX } from 'lucide-react';
import { canAccess, hasPermission, ROLE_LABELS } from '@blimo/shared';
import { useMe, useRefreshMe, useTwoFactorActions } from '@blimo/api-client/react';
import { useApi } from '@blimo/api-client/react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  PageHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from '@blimo/ui';
import { ChangePasswordForm, TwoFactorSettings } from '@blimo/ui/auth';

/**
 * Placeholder for every navigation target until Phase D/E implements it.
 * @param {{ item: import('../nav.js').NavItem }} props
 */
export function PlaceholderPage({ item }) {
  const { data: me } = useMe();
  if (!canAccess(me, item.access)) return <ForbiddenPage />;
  const Icon = item.icon ?? Construction;
  return (
    <>
      <PageHeader title={item.label} description={item.features ? `Funktionen: ${item.features}` : undefined} />
      <EmptyState
        icon={Icon}
        title="Wird in Phase D/E umgesetzt"
        description="Diese Ansicht ist vorbereitet. Inhalte und Aktionen folgen mit der Anbindung der entsprechenden API-Endpunkte."
      />
    </>
  );
}

/**
 * Renders children only with the given permission (UX – the API checks again).
 * @param {{ permission: string, children: import('react').ReactNode }} props
 */
export function RequirePermission({ permission, children }) {
  const { data: me } = useMe();
  if (!hasPermission(me, permission)) return <ForbiddenPage />;
  return children;
}

export function ForbiddenPage() {
  return (
    <>
      <PageHeader title="Kein Zugriff" />
      <EmptyState
        icon={ShieldX}
        title="Für diese Seite fehlt Ihnen die Berechtigung"
        description="Wenden Sie sich an eine Administratorin oder einen Administrator, wenn Sie Zugriff benötigen."
        action={
          <Button asChild variant="outline">
            <Link to="/">Zum Dashboard</Link>
          </Button>
        }
      />
    </>
  );
}

export function NotFoundPage() {
  return (
    <div className="p-6">
      <PageHeader title="Seite nicht gefunden" />
      <EmptyState
        title="Diese Seite gibt es nicht"
        description="Prüfen Sie die Adresse oder kehren Sie zum Dashboard zurück."
        action={
          <Button asChild variant="outline">
            <Link to="/">Zum Dashboard</Link>
          </Button>
        }
      />
    </div>
  );
}

const PROFILE_TABS = ['profil', 'passwort', 'zwei-faktor'];

export function ProfilePage() {
  const api = useApi();
  const { data: me } = useMe();
  const search = useSearch({ strict: false });
  const twoFactorActions = useTwoFactorActions();
  const refreshMe = useRefreshMe();
  const tab = PROFILE_TABS.includes(search.tab) ? search.tab : 'profil';

  if (!me) return null;
  return (
    <>
      <PageHeader title="Mein Profil" />
      <Tabs key={tab} defaultValue={tab}>
        <TabsList>
          <TabsTrigger value="profil">Profil</TabsTrigger>
          <TabsTrigger value="passwort">Passwort</TabsTrigger>
          <TabsTrigger value="zwei-faktor">Zwei-Faktor</TabsTrigger>
        </TabsList>
        <TabsContent value="profil">
          <Card className="max-w-xl">
            <CardHeader>
              <CardTitle as="h2">Persönliche Angaben</CardTitle>
              <CardDescription>Änderungen an Name und E-Mail-Adresse nimmt die Administration vor.</CardDescription>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-[8rem_1fr] gap-x-4 gap-y-2 text-sm">
                <dt className="text-muted-foreground">Name</dt>
                <dd>{me.name}</dd>
                <dt className="text-muted-foreground">E-Mail</dt>
                <dd className="break-all">{me.email}</dd>
                <dt className="text-muted-foreground">Rollen</dt>
                <dd className="flex flex-wrap gap-1">
                  {me.roles?.map((role) => (
                    <Badge key={role} variant="secondary">
                      {ROLE_LABELS[role] ?? role}
                    </Badge>
                  ))}
                </dd>
              </dl>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="passwort">
          <Card className="max-w-xl">
            <CardHeader>
              <CardTitle as="h2">Passwort ändern</CardTitle>
              <CardDescription>Andere angemeldete Sitzungen werden dabei abgemeldet.</CardDescription>
            </CardHeader>
            <CardContent>
              <ChangePasswordForm onSubmit={(values) => api.auth.updatePassword(values)} onSuccess={() => toast.success('Passwort geändert')} />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="zwei-faktor">
          <Card className="max-w-xl">
            <CardHeader>
              <CardTitle as="h2">Zwei-Faktor-Authentifizierung</CardTitle>
              <CardDescription>Schützt Ihr Konto zusätzlich mit einem Einmalcode.</CardDescription>
            </CardHeader>
            <CardContent>
              <TwoFactorSettings
                enabled={Boolean(me.two_factor?.enabled)}
                required={Boolean(me.two_factor?.required)}
                actions={twoFactorActions}
                onChanged={refreshMe}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </>
  );
}
